// ─────────────────────────────────────────────────────────────────────────
// SaludPrecios — panel "💰 Salud de precios" en /quotations (28-ago-2026,
// pedido Maxi). La UF sube y los precios de las piezas llevan meses o años
// congelados: cada pieza con su precio vigente, desde cuándo no cambia y
// cuánto subió la UF desde entonces — munición para defender los precios
// al cotizar. Fuente: GET /sii/salud-precios (PieceInstance + mindicador.cl).
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import { formatPrice } from "../../utils";

const fmt = n => `$${formatPrice(Math.round(n || 0))}`;
const num = { fontVariantNumeric: "tabular-nums" };

const MESES_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const mesAnio = d => {
  if (!d) return "—";
  const f = new Date(d);
  return `${MESES_ES[f.getMonth()]}-${f.getFullYear()}`;
};

// Semáforo por meses congelado: ≥12 rojo · ≥6 ámbar · <6 verde
const semaforo = meses =>
  meses >= 12 ? { color: "#e74c3c", bg: "#2a0d0d", border: "#4a1a1a", label: "congelado" }
  : meses >= 6 ? { color: "#f39c12", bg: "#2a1d08", border: "#4a3410", label: "ojo" }
  : { color: "#2ecc71", bg: "#0d2a15", border: "#0d4020", label: "al día" };

const S = {
  card: { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
};

const ORDENES = [
  ["ingreso", "Por ingresos"],
  ["meses", "Más congeladas"],
  ["subida", "Mayor ajuste sugerido"],
  ["erosion", "Más erosión UF"],
];

// CSV de precios sugeridos (15-sep): para que el vendedor lo revise con calma o lo imprima.
const descargarCsv = items => {
  const cab = ["SKU", "Pieza", "Precio actual", "Sin cambio desde", "Meses", "UF %", "Precio sugerido", "Diferencia", "Unidades 12m", "Ingreso 12m", "Por qué"];
  const esc = v => '"' + String(v ?? "").replace(/"/g, '""') + '"';
  const filas = items.map(it => [it.sku || "", it.nombre, it.precioActual, mesAnio(it.fechaDesde), it.mesesCongelado,
    it.erosionPct ?? "", it.precioSugerido ?? "", it.subidaPesos ?? "", it.unidades12m, it.ingreso12m, it.porQue || ""].map(esc).join(";"));
  const contenido = String.fromCharCode(0xfeff) + [cab.map(esc).join(";"), ...filas].join(String.fromCharCode(10));
  const blob = new Blob([contenido], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "precios_sugeridos_" + new Date().toISOString().slice(0, 10) + ".csv";
  a.click();
  URL.revokeObjectURL(a.href);
};

const SaludPrecios = ({ abierto = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [orden, setOrden] = useState("ingreso"); // ingreso | meses | erosion | subida
  const [verPorQue, setVerPorQue] = useState(abierto ? "todos" : null); // clave del ítem abierto, o "todos"
  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState(""); // búsqueda aplicada (debounced, igual que VentasPorPieza)

  useEffect(() => { const t = setTimeout(() => setQ(buscar.trim().toLowerCase()), 400); return () => clearTimeout(t); }, [buscar]);

  useEffect(() => {
    let vivo = true;
    backend.get("sii/salud-precios")
      .then(r => { if (vivo) setData(r.data); })
      .catch(e => { if (vivo) setError(e?.response?.data?.error || e.message); });
    return () => { vivo = false; };
  }, []);

  // Resumen: piezas congeladas ≥12m + erosión UF promedio PONDERADA por ingreso
  // (las piezas que más facturan pesan más en la frase clave).
  const resumen = useMemo(() => {
    if (!data?.items) return null;
    const congeladas = data.items.filter(i => i.mesesCongelado >= 12);
    const conUf = data.items.filter(i => i.erosionPct != null && i.ingreso12m > 0);
    const pesoTotal = conUf.reduce((s, i) => s + i.ingreso12m, 0);
    const erosionPond = pesoTotal > 0
      ? conUf.reduce((s, i) => s + i.erosionPct * i.ingreso12m, 0) / pesoTotal
      : null;
    return { congeladas: data.items.length ? congeladas.length : null, erosionPond };
  }, [data]);

  if (error) return <div style={{ color: "#e74c3c", fontSize: 12, padding: 12 }}>Error: {error}</div>;
  if (!data) return <div style={{ color: "#8a8a8a", fontSize: 12, padding: 12 }}>Calculando la salud de los precios…</div>;

  const filtrados = data.items.filter(i => {
    if (!q) return true;
    return (i.nombre || "").toLowerCase().includes(q) ||
           (i.sku || "").toLowerCase().includes(q) ||
           (i.sku || "").replace(/\s+/g, "").includes(q.replace(/\s+/g, ""));
  });
  const items = [...filtrados].sort((a, b) =>
    orden === "meses" ? b.mesesCongelado - a.mesesCongelado
    : orden === "subida" ? (b.subidaPesos || 0) * (b.unidades12m || 0) - (a.subidaPesos || 0) * (a.unidades12m || 0)
    : orden === "erosion" ? (b.erosionPct ?? -1) - (a.erosionPct ?? -1)
    : b.ingreso12m - a.ingreso12m
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* Tarjeta resumen: la frase para defender los precios */}
      <div style={{ ...S.card, border: "1px solid #4a1a1a", background: "#241416" }}>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "baseline" }}>
          <div>
            <div style={S.label}>Precios congelados ≥ 12 meses</div>
            <div style={{ fontSize: resumen?.congeladas == null ? 14 : 26, fontWeight: 800, color: resumen?.congeladas == null ? "#8a8a8a" : "#e74c3c", ...num }}>{resumen?.congeladas == null ? "—  sin datos" : `${resumen.congeladas} piezas`}</div>
          </div>
          {resumen?.erosionPond != null && (
            <div>
              <div style={S.label}>Erosión UF (ponderada por ingreso)</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#f39c12", ...num }}>+{resumen.erosionPond.toFixed(1)}%</div>
            </div>
          )}
          {data.ufHoy != null && (
            <>
              <div>
                <div style={S.label}>Si se ajustan los precios sugeridos</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#2ecc71", ...num }}>
                  {data.extraAnualSiAjusta != null ? `+${fmt(data.extraAnualSiAjusta)} / año` : "—"}
                </div>
                <div style={{ fontSize: 10, color: "#8a8a8a" }}>sobre las unidades vendidas en los últimos 12 meses</div>
                <button onClick={() => descargarCsv(data.items)} className="chip-touch no-touch-min t-02"
                  style={{ marginTop: 8, background: "#1e1e1e", color: "#ddd", border: "1px solid #333", borderRadius: 6, padding: "7px 10px", fontSize: 11, cursor: "pointer" }}>
                  ⬇ Descargar listado de precios sugeridos (CSV)
                </button>
              </div>
              <div>
                <div style={S.label}>UF hoy</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#cbd5e1", ...num }}>${formatPrice(Math.round(data.ufHoy))}</div>
              </div>
            </>
          )}
        </div>
        <div style={{ fontSize: 12, color: "#e0a0a0", marginTop: 8 }}>
          {resumen?.erosionPond != null
            ? <>La UF subió <strong style={num}>+{resumen.erosionPond.toFixed(1)}%</strong> en promedio desde que estos precios no se tocan: cada mes que pasa, la misma pieza se vende más barata en plata real.</>
            : "Sin la serie UF en este momento (mindicador.cl no respondió) — igual puedes ver desde cuándo no cambia cada precio."}
        </div>
      </div>

      {/* Controles: orden + buscador */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {ORDENES.map(([k, l]) => (
            <button key={k} onClick={() => setOrden(k)} className="chip-touch no-touch-min t-02"
              style={{ background: orden === k ? "#3a1114" : "#1e1e1e", color: orden === k ? "#ff8a8f" : "#999",
                border: `1px solid ${orden === k ? "#e30613" : "#333"}`, borderRadius: 6, padding: "9px 12px",
                minHeight: 38, fontSize: 11, cursor: "pointer" }}>
              {l}
            </button>
          ))}
        </div>
        <input value={buscar} onChange={e => setBuscar(e.target.value)}
          placeholder="🔎 Filtrar por pieza o código (SKU)…"
          style={{ flex: "1 1 200px", background: "#161616", border: "1px solid #333", borderRadius: 6,
            padding: "9px 11px", minHeight: 38, boxSizing: "border-box", fontSize: 12, color: "#ddd" }} />
      </div>

      {/* Lista de piezas */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, idx) => {
          const sem = semaforo(it.mesesCongelado);
          return (
            <div key={it.clave || idx} style={{ ...S.card, padding: "10px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {it.nombre}
                  </div>
                  <div style={{ fontSize: 10, color: "#8a8a8a", ...num }}>
                    {it.sku ? `SKU ${it.sku} · ` : ""}{it.nCambios} cambio{it.nCambios === 1 ? "" : "s"} de precio en la historia
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#fff", ...num }}>{fmt(it.precioActual)}</div>
                  {it.precioSugerido != null && it.precioSugerido !== it.precioActual && (
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#2ecc71", ...num }} title="Precio sugerido para mantener el valor real (UF)">
                      sugerido {fmt(it.precioSugerido)} <span style={{ fontSize: 10, color: "#8a8a8a" }}>(+{it.subidaPct}%)</span>
                    </div>
                  )}
                  {it.precioCatalogo != null && Math.round(it.precioCatalogo) !== Math.round(it.precioActual) && (
                    <div style={{ fontSize: 10, color: "#f39c12", ...num }} title="El catálogo dice otra cosa que la última venta">
                      catálogo {fmt(it.precioCatalogo)}
                    </div>
                  )}
                </div>
                <span
                  onClick={() => navigate(`/pieces?search=${encodeURIComponent(it.sku || it.nombre || "")}`)}
                  title="Ajustar precio en el catálogo"
                  style={{ background: sem.bg, color: sem.color, border: `1px solid ${sem.border}`,
                    borderRadius: 5, padding: "3px 9px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap",
                    flexShrink: 0, cursor: "pointer", ...num }}>
                  {it.mesesCongelado} m {sem.label}
                </span>
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6, fontSize: 11, color: "#b8b8b8", ...num }}>
                <span>Sin cambio desde <strong style={{ color: "#ddd" }}>{mesAnio(it.fechaDesde)}</strong> ({it.mesesCongelado} meses)</span>
                {it.erosionPct != null && (
                  <span style={{ color: it.erosionPct >= 10 ? "#e74c3c" : it.erosionPct >= 5 ? "#f39c12" : "#8a8a8a", fontWeight: 700 }}>
                    UF +{it.erosionPct}% desde entonces
                  </span>
                )}
                <span style={{ marginLeft: "auto" }}>
                  12m: <strong style={{ color: "#2ecc71" }}>{fmt(it.ingreso12m)}</strong> · {it.unidades12m.toLocaleString("es-CL")} u
                </span>
                {it.porQue && (
                  <button onClick={() => setVerPorQue(v => (v === it.clave || v === "todos") ? (v === "todos" ? "__ninguno" : null) : it.clave)}
                    style={{ background: "transparent", color: "#5dade2", border: "none", padding: 0, fontSize: 11, cursor: "pointer" }}>
                    {(verPorQue === it.clave || verPorQue === "todos") ? "ocultar" : "¿por qué?"}
                  </button>
                )}
              </div>
              {it.porQue && (verPorQue === it.clave || verPorQue === "todos") && (
                <div style={{ marginTop: 6, padding: "8px 10px", background: "#161616", border: "1px solid #2a2a2a", borderRadius: 6, fontSize: 12, color: "#ddd", lineHeight: 1.45 }}>
                  {it.porQue}
                </div>
              )}
            </div>
          );
        })}
        {!items.length && (
          <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>
            Sin piezas{q ? " para esa búsqueda" : " con historia de precios"}.
          </div>
        )}
      </div>

      <div style={{ fontSize: 10, color: "#8a8a8a", padding: "4px 2px" }}>
        Historia calculada sobre la facturación electrónica real 2020-2026 (cosecha Enternet) + el registro del ERP. "Sin cambio desde" es la fecha del último cambio de precio REAL en 6,7 años de ventas.
      </div>
    </div>
  );
};

export default SaludPrecios;
